import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { useMonthlyMerchantInvoices, MonthlyInvoiceItem } from '@/shared/hooks/useMonthlyMerchantInvoices';
import { useToast } from '@/shared/hooks/use-toast';

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function feeLabel(item: MonthlyInvoiceItem) {
  if (item.fee_tier === 'absorbed') {
    const total = (item.technology_fee_cents + item.merchant_fee_cents) / 100;
    return `Tech + Merchant fee: $${total.toFixed(2)}`;
  }
  return `Merchant fee: $${(item.merchant_fee_cents / 100).toFixed(2)}`;
}

export default function MonthlyFeesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    monthlyInvoices,
    loading,
    refreshing,
    refresh,
    fetchInvoiceItems,
    createPaymentUrl,
    totalPendingCents,
  } = useMonthlyMerchantInvoices();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [itemsMap, setItemsMap] = useState<Record<string, MonthlyInvoiceItem[]>>({});
  const [itemsLoading, setItemsLoading] = useState<Record<string, boolean>>({});
  const [payingId, setPayingId] = useState<string | null>(null);

  const handleToggleExpand = useCallback(async (invoiceId: string, billingMonth: string) => {
    if (expandedId === invoiceId) { setExpandedId(null); return; }
    setExpandedId(invoiceId);
    if (!itemsMap[invoiceId]) {
      setItemsLoading(prev => ({ ...prev, [invoiceId]: true }));
      const items = await fetchInvoiceItems(billingMonth);
      setItemsMap(prev => ({ ...prev, [invoiceId]: items }));
      setItemsLoading(prev => ({ ...prev, [invoiceId]: false }));
    }
  }, [expandedId, itemsMap, fetchInvoiceItems]);

  const handlePay = useCallback(async (invoiceId: string) => {
    try {
      setPayingId(invoiceId);
      const { payment_url } = await createPaymentUrl(invoiceId);
      window.open(payment_url, '_blank');
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message || 'Failed to generate payment link', variant: 'destructive' });
    } finally {
      setPayingId(null);
    }
  }, [createPaymentUrl, toast]);

  const sortedInvoices = [...monthlyInvoices].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    return b.billing_month.localeCompare(a.billing_month);
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')} className="h-8 w-8 p-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Invoice Fees Due</h1>
          <p className="text-sm text-muted-foreground">Monthly processing fees for Option B &amp; C invoices</p>
        </div>
      </div>

      {/* Summary card */}
      <Card className="border-2">
        <CardContent className="pt-6 pb-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">Total Pending Fees</p>
          <p className={`text-4xl font-bold mb-2 ${totalPendingCents > 0 ? 'text-red-600' : 'text-foreground'}`}>
            {formatMoney(totalPendingCents)}
          </p>
          <p className="text-xs text-muted-foreground">
            Fees from invoices where you absorbed processing costs (Option B or C)
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={refresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </CardContent>
      </Card>

      {/* Invoice list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading monthly fees...</div>
      ) : sortedInvoices.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <p className="font-medium text-muted-foreground">No fees yet</p>
          <p className="text-sm text-muted-foreground">
            Fee invoices appear here when you create invoices using Option B (Hybrid) or Option C (You Pay All).
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Monthly Breakdown</p>

          {sortedInvoices.map(invoice => {
            const isExpanded = expandedId === invoice.id;
            const items = itemsMap[invoice.id] || [];
            const loadingItems = itemsLoading[invoice.id];

            return (
              <Card key={invoice.id} className="overflow-hidden">
                <CardContent className="p-4 space-y-3">
                  {/* Card header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-base">{invoice.billing_month}</p>
                      <p className="text-sm text-muted-foreground">
                        {invoice.invoice_count} {invoice.invoice_count === 1 ? 'invoice' : 'invoices'}
                      </p>
                    </div>
                    <Badge
                      className={
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                          : invoice.status === 'overdue'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                      }
                    >
                      {invoice.status.toUpperCase()}
                    </Badge>
                  </div>

                  {/* Fee breakdown */}
                  <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Platform Fees</span>
                      <span>{formatMoney(invoice.platform_fees_cents)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Merchant Fees</span>
                      <span>{formatMoney(invoice.merchant_fees_cents)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between text-sm font-semibold">
                      <span>Total</span>
                      <span className={invoice.status === 'pending' ? 'text-red-600' : 'text-green-600'}>
                        {formatMoney(invoice.total_fees_cents)}
                      </span>
                    </div>
                  </div>

                  {/* Expand/collapse */}
                  <button
                    className="w-full flex items-center justify-center gap-1.5 text-sm text-primary border-t pt-2"
                    onClick={() => handleToggleExpand(invoice.id, invoice.billing_month)}
                  >
                    {isExpanded ? 'Hide' : 'Show'} contributing invoices
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>

                  {/* Expanded items */}
                  {isExpanded && (
                    <div className="space-y-2">
                      {loadingItems ? (
                        <p className="text-xs text-muted-foreground text-center py-2">Loading...</p>
                      ) : items.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-2">No invoices found</p>
                      ) : items.map(item => (
                        <div
                          key={item.id}
                          className="flex items-start justify-between rounded-lg border p-2.5 gap-3 bg-background"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold">{item.invoice_number}</p>
                            {item.customer_name && (
                              <p className="text-xs text-muted-foreground">{item.customer_name}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {feeLabel(item)} &bull; {item.fee_tier === 'absorbed' ? 'Option C' : 'Option B'}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0 space-y-0.5">
                            <p className="text-xs font-semibold text-red-600">
                              -{formatMoney(item.fee_tier === 'absorbed'
                                ? item.technology_fee_cents + item.merchant_fee_cents
                                : item.merchant_fee_cents)}
                            </p>
                            <p className="text-xs text-muted-foreground">inv. {formatMoney(item.amount_cents)}</p>
                            {item.refunded_at && (
                              <span className="inline-block text-[10px] font-bold text-red-600 bg-red-50 dark:bg-red-900/20 px-1 py-0.5 rounded">
                                REFUNDED
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pay button */}
                  {invoice.status === 'pending' && (
                    <Button
                      className="w-full"
                      onClick={() => handlePay(invoice.id)}
                      disabled={payingId === invoice.id}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {payingId === invoice.id ? 'Opening...' : `Pay ${formatMoney(invoice.total_fees_cents)}`}
                    </Button>
                  )}

                  {/* Paid date */}
                  {invoice.paid_at && (
                    <p className="text-xs text-green-600 text-center">
                      Paid on {formatDate(invoice.paid_at)}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
